import { DefaultMap, hasOwnProperty } from "@glideapps/ts-necessities";
import { AutoWatchable, Watchable } from "common/change-obserable";
import type { AppEnvironment } from "common/components/types";
import type { EminenceFlags } from "common/Database/eminence";
import { eminenceFull } from "common/Database/eminence";
import type { ComponentType, FunctionComponent } from "react";
import { getAppEminenceFlags } from "store/types";
import type { Subtract } from "utility-types";

import { useAppID } from "./use-app-id";
import { useChangeObservable } from "./use-change-observable";
import { useDeepEqual } from "./use-deep-equal";

type PlayerEminenceFlags = DefaultMap<string, Watchable<EminenceFlags>>;
const playerWatchedFlags: PlayerEminenceFlags = new DefaultMap(k => {
    const flags =
        hasOwnProperty(window, "eminenceFlags") && window.eminenceFlags !== undefined
            ? (window.eminenceFlags as EminenceFlags)
            : eminenceFull;
    return k !== "" ? new AutoWatchable(flags, async () => getAppEminenceFlags(k), 60 * 1000) : new Watchable(flags);
});

export function getPlayerEminenceFlags(appID: string): EminenceFlags {
    return playerWatchedFlags.get(appID).current;
}

export function usePlayerEminenceFlags(appID: string): EminenceFlags {
    return useDeepEqual(useChangeObservable(playerWatchedFlags.get(appID)));
}

interface WithPlayerEminenceFlagsProps {
    readonly eminenceFlags: EminenceFlags;
}

interface WrapperProps {
    readonly appID: string;
    readonly appEnvironment: AppEnvironment;
}

interface Props extends Partial<WrapperProps>, WithPlayerEminenceFlagsProps {}

export const withPlayerEminenceFlags = <P extends Props>(
    Component: ComponentType<P>
): FunctionComponent<Subtract<P, WithPlayerEminenceFlagsProps>> => p => {
    const routerAppID = useAppID();
    const appID = p.appID ?? p.appEnvironment?.appID ?? routerAppID ?? (window as any).appID ?? "";
    const eminenceFlags = usePlayerEminenceFlags(appID);
    return <Component {...(p as P)} eminenceFlags={eminenceFlags} />;
};
